import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeletedNetworkResponse, WifiInfo, WifiNetwork, WifiNetworkResponse, WifiNetworks, WifiStatus } from '@models/wifi-network/wifi-network.model';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { BedService } from '@services/bed.service';
import { FuzionService } from '@services/fuzion.service';
import { WifiService } from '@services/wifi.service';
import { FunctionsHelper } from '@shared/utils/helpers/functions.helper';
import { Observable, catchError, delay, filter, forkJoin, from, map, of, take, tap, throwError } from 'rxjs';
import { BedPresenceFlag, Side } from 'src/app/core/fuzion/enums';
import { GetWifiInformationResponse, GetWifiSetupInformationResponse } from 'src/app/core/fuzion/model';
import { AddWifiNetwork, BaselineBed, DeleteWifiNetwork, LoadWifiInfo, LoadWifiNetworks, LoadWifiStatus, ResetSettingsState, SelectBed } from './settings.actions';
import { SettingsStateModel, defaultState } from './settings.model';
import { BedsSelectors } from '@store/beds/beds.selectors';
import { BedStatus } from '@models/bed/bed-status.model';
import { BamkeyError } from 'src/app/core/fuzion/client';

@State<SettingsStateModel>({
  name: 'settings',
  defaults: defaultState
})
@Injectable()
export class SettingsState {

  constructor(private wifiService: WifiService, private bedService: BedService, private fuzionService: FuzionService, private store: Store) { }

  @Action(SelectBed)
  selectBed(ctx: StateContext<SettingsStateModel>, action: SelectBed): void {
    ctx.patchState({ selectedBedId: action.bed.bedId });
    // TODO: Should not be called for unconfigured beds
    ctx.dispatch(new LoadWifiNetworks());
    ctx.dispatch(new LoadWifiStatus(action.bed));
    ctx.dispatch(new LoadWifiInfo(action.bed));
  }

  @Action(LoadWifiNetworks)
  loadWifiNetworks(ctx: StateContext<SettingsStateModel>): Observable<WifiNetworks> {
    ctx.patchState({ loading: true });
    return this.wifiService.getWifiNetworks().pipe(
      tap({
        next: (result: any) => {
          ctx.patchState({ loading: false, wifiNetworks: result.networks.map((wifi: WifiNetwork) => Object.assign(new WifiNetwork(), wifi)) });
        },
        error: error => this.handleError(ctx, error)
      })
    );
  }

  @Action(LoadWifiStatus)
  loadWifiStatus(ctx: StateContext<SettingsStateModel>, action: LoadWifiStatus): Observable<WifiStatus | GetWifiInformationResponse> {
    const wifiStatus = action.bed.isFuzion ? this.fuzionService.getWifiStatus(action.bed.accountId, action.bed.bedId) : this.wifiService.getWifiStatus(action.bed.bedId);
    const selectedBedId = ctx.getState().selectedBedId;
    let selectedBedStatus: BedStatus | undefined = undefined;
    this.store.select(BedsSelectors.bedsStatus).pipe(
      map(bedsStatus => bedsStatus.find(bed => bed.bedId === selectedBedId)),
      filter((bedStatus): bedStatus is BedStatus => !!bedStatus),
      take(1)
    ).subscribe(bedsStatus => {
      selectedBedStatus = bedsStatus;
    });

    return from(wifiStatus).pipe(
      tap({
        next: (response: WifiStatus | GetWifiInformationResponse) => {
          const result = action.bed.isFuzion ? (response as GetWifiInformationResponse).wifiStatus as WifiStatus : response as WifiStatus;
          ctx.setState(
            patch({ 
              wifiStatus: insertItem({ bedId: action.bed.bedId, ...result } as WifiStatus),
              networkName: result.ssid.slice(0, 33)
            }
          ));
        },
        error: (err) => {
          this.handleError(ctx, err);
          ctx.patchState({ networkName: selectedBedStatus?.statusDesc });
        }
      })
    );
  }

  @Action(AddWifiNetwork)
  addWifiNetwork(ctx: StateContext<SettingsStateModel>, action: AddWifiNetwork): Observable<WifiNetworkResponse> {
    ctx.patchState({ loading: true });
    return this.wifiService.addWifiNetwork(action.payload).pipe(
      tap({
        next: () => {
          ctx.patchState({ loading: false });
          ctx.dispatch(new LoadWifiNetworks());
        },
        error: error => this.handleError(ctx, error)
      })
    );
  }

  @Action(DeleteWifiNetwork)
  deleteWifiNetwork(ctx: StateContext<SettingsStateModel>, action: DeleteWifiNetwork): Observable<DeletedNetworkResponse> {
    ctx.patchState({ loading: true });
    return this.wifiService.deleteWifiNetwork(action.wifiId).pipe(
      tap({
        next: () => {
          ctx.patchState({ loading: false });
          ctx.dispatch(new LoadWifiNetworks());
        },
        error: error => this.handleError(ctx, error)
      })
    );
  }

  @Action(BaselineBed)
  baselineBed(ctx: StateContext<SettingsStateModel>, action: BaselineBed): Observable<object> {
    ctx.patchState({ baselineInProgress: true, error: null });
    let leftSleeperBaseline = of({});
    let rightSleeperBaseline = of({});

    if (action.bed.sleeperLeftId) {
      leftSleeperBaseline = action.bed.isFuzion ? this.fuzionService.setBedPresence(action.bed.accountId, action.bed.bedId, Side.Left, BedPresenceFlag.Out) : this.bedService.baselineBed(action.bed.sleeperLeftId);
    }

    if (action.bed.sleeperRightId) {
      rightSleeperBaseline = action.bed.isFuzion ? this.fuzionService.setBedPresence(action.bed.accountId, action.bed.bedId, Side.Right, BedPresenceFlag.Out) : this.bedService.baselineBed(action.bed.sleeperRightId);
    }

    return forkJoin([leftSleeperBaseline, rightSleeperBaseline]).pipe(
      delay(1000),
      catchError(() => { throw FunctionsHelper.createGenericError(-404, 'Baseline error'); }),
      tap({
        next: () => ctx.patchState({ baselineInProgress: false }),
        error: (error) => {
          return this.handleError(ctx, error);
        }
      })
    );
  }

  @Action(LoadWifiInfo)
  loadWifiInfo(ctx: StateContext<SettingsStateModel>, action: LoadWifiInfo): Observable<WifiInfo | GetWifiSetupInformationResponse> {
    const wifiInfo$ = action.bed.isFuzion
      ? this.fuzionService.getWifiSetupVersion(action.bed.accountId, action.bed.bedId)
      : this.wifiService.getWifiInfo(action.bed.bedId);

    return from(wifiInfo$).pipe(
      tap({
        next: (response: WifiInfo | GetWifiSetupInformationResponse) => {
          const result = action.bed.isFuzion
            ? (response as GetWifiSetupInformationResponse).wifiSetupVersion as WifiInfo
            : response as WifiInfo;

          this.updateWifiInfoState(ctx, action.bed.bedId, { bedId: action.bed.bedId, ...result } as WifiInfo);

        },
        error: (err) => {
          err instanceof BamkeyError ? this.handleBamkeyError(ctx, err) :  this.handleError(ctx, err);
          // -1 to handle the case when API fails for offline and not updated beds
          this.updateWifiInfoState(ctx, action.bed.bedId, { bedId: action.bed.bedId, version: -1 } as WifiInfo);
        }
      })
    );
  }


  @Action(ResetSettingsState)
  resetBedState(ctx: StateContext<SettingsStateModel>): void {
    ctx.setState({ ...defaultState });
  }

  private handleError(ctx: StateContext<SettingsStateModel>, err: HttpErrorResponse): Observable<HttpErrorResponse> {
    ctx.patchState({ error: FunctionsHelper.createSiqError(err.error.Error.Code, err.error.Error.Message), loading: false });
    return throwError(() => err);
  }

  private handleBamkeyError(ctx: StateContext<SettingsStateModel>, err: BamkeyError): Observable<BamkeyError> {
    ctx.patchState({ error: FunctionsHelper.createSiqError(err.status, err.description), loading: false });
    return throwError(() => err);
  }

  private updateWifiInfoState(ctx: StateContext<SettingsStateModel>, bedId: string, wifiInfoItem: WifiInfo): void {
    const currentState = ctx.getState();
    const existingItem = currentState.wifiInfo.find(item => item.bedId === bedId);

    if (existingItem) {
      // Update the existing wifi info for the bed
      ctx.setState(
        patch({
          wifiInfo: updateItem<WifiInfo>(
            item => item.bedId === bedId,
            wifiInfoItem
          )
        })
      );
    } else {
      // Insert the new wifi info for the bed
      ctx.setState(
        patch({
          wifiInfo: insertItem(wifiInfoItem)
        })
      );
    }
  }
}
